import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  ArrowLeft,
  CalendarDays,
  Images,
  LayoutDashboard,
  Menu,
  RefreshCw,
  Scissors,
  Users,
} from 'lucide-react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import {
  ActionButton,
  ErrorState,
  LoadingState,
  StatusBadge,
} from '../../components/superadmin/SuperAdminUI';

const ACCENT = '#7d6633';

const statusVariant = (status) => {
  if (status === 'active') return 'success';
  if (status === 'trial') return 'warning';
  if (status === 'cancelled' || status === 'suspended') return 'danger';
  return 'default';
};

const SuperAdminShopWorkspace = () => {
  const { id: shopId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { getShops, getAdmins } = useSuperadminAuth();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const sections = useMemo(() => ([
    { id: 'overview', label: 'Overview', path: `/superadmin/shops/${shopId}/overview`, icon: <LayoutDashboard className="h-5 w-5" /> },
    { id: 'usage', label: 'Usage Summary', path: `/superadmin/shops/${shopId}/usage`, icon: <Activity className="h-5 w-5" /> },
    { id: 'bookings', label: 'Bookings', path: `/superadmin/shops/${shopId}/bookings`, icon: <CalendarDays className="h-5 w-5" /> },
    { id: 'barbers', label: 'Barbers', path: `/superadmin/shops/${shopId}/barbers`, icon: <Scissors className="h-5 w-5" /> },
    { id: 'clients', label: 'Clients', path: `/superadmin/shops/${shopId}/clients`, icon: <Users className="h-5 w-5" /> },
    { id: 'ai-images', label: 'AI Images', path: `/superadmin/shops/${shopId}/ai-images`, icon: <Images className="h-5 w-5" /> },
  ]), [shopId]);

  const activeSection = useMemo(
    () => sections.find((section) => location.pathname === section.path)?.id || 'overview',
    [location.pathname, sections]
  );

  const loadShop = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [shopsResponse, adminsResponse] = await Promise.all([
        getShops({ q: shopId, page: 1, pageSize: 100 }),
        getAdmins({ shop_id: shopId, page: 1, pageSize: 100 }),
      ]);

      if (!shopsResponse?.ok || !adminsResponse?.ok) {
        throw new Error('Failed to load shop workspace');
      }

      const resolvedShop =
        (shopsResponse.data || []).find((entry) => String(entry.id) === String(shopId)) ||
        (adminsResponse.data || [])
          .flatMap((entry) => entry.shops || [])
          .find((entry) => String(entry.id) === String(shopId)) ||
        null;

      if (!resolvedShop) {
        throw new Error('Shop not found');
      }

      setShop(resolvedShop);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load shop workspace');
      setShop(null);
    } finally {
      setLoading(false);
    }
  }, [getAdmins, getShops, shopId]);

  useEffect(() => {
    loadShop();
  }, [loadShop]);

  const renderNavButton = (section, isMobile = false) => {
    const isActive = activeSection === section.id;

    return (
      <motion.button
        key={section.id}
        type="button"
        onClick={() => {
          navigate(section.path);
          if (isMobile) {
            setMobileMenuOpen(false);
          }
        }}
        className={`relative flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 ${
          isActive
            ? 'bg-[--accent]/10 text-[--accent]'
            : 'text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800/50'
        }`}
        style={{ '--accent': ACCENT }}
        whileHover={isMobile ? undefined : { scale: 1.02 }}
        whileTap={isMobile ? undefined : { scale: 0.98 }}
      >
        <div style={{ color: isActive ? ACCENT : 'inherit' }}>{section.icon}</div>
        <span className="flex-1 text-left">{section.label}</span>
        {isActive ? (
          <motion.div
            layoutId={isMobile ? 'shop-workspace-mobile-indicator' : 'shop-workspace-desktop-indicator'}
            className={`${isMobile ? 'ml-auto' : 'absolute right-2'} h-2 w-2 rounded-full`}
            style={{ backgroundColor: ACCENT }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          />
        ) : null}
      </motion.button>
    );
  };

  return (
    <div className="w-full h-full space-y-6">
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
              <Link to="/superadmin/dashboard" className="transition hover:text-zinc-900 dark:hover:text-zinc-100">
                Super Admin
              </Link>
              <span>/</span>
              <Link to="/superadmin/shops" className="transition hover:text-zinc-900 dark:hover:text-zinc-100">
                Shops
              </Link>
              <span>/</span>
              <span className="text-zinc-900 dark:text-zinc-100">{shop?.name || `Shop #${shopId}`}</span>
            </div>

            {loading ? (
              <div className="space-y-2">
                <div className="h-8 w-56 animate-pulse rounded-xl bg-white/50 dark:bg-zinc-800/50" />
                <div className="h-5 w-80 animate-pulse rounded-xl bg-white/40 dark:bg-zinc-800/40" />
              </div>
            ) : (
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{shop?.name || `Shop #${shopId}`}</h1>
                  {shop?.subscription_status ? (
                    <StatusBadge status={shop.subscription_status} variant={statusVariant(shop.subscription_status)} />
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                  Navigate tenant-level overview, usage, bookings, staff, clients, and AI imagery from a single shop workspace.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/superadmin/shops"
              className="hidden items-center gap-2 rounded-2xl border border-white/10 bg-white/70 px-4 py-3 text-sm font-medium text-zinc-700 transition hover:bg-white dark:bg-zinc-900/60 dark:text-zinc-100 md:inline-flex"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to explorer
            </Link>
            <ActionButton variant="accent" size="md" onClick={loadShop} disabled={loading}>
              <span className="inline-flex items-center gap-2">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </span>
            </ActionButton>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((current) => !current)}
              className="rounded-xl border border-white/20 bg-white/50 px-4 py-2 text-zinc-900 backdrop-blur-sm dark:border-white/10 dark:bg-zinc-800/50 dark:text-zinc-100 lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {error ? <ErrorState error={error} onRetry={loadShop} /> : null}

      <AnimatePresence>
        {mobileMenuOpen ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden lg:hidden"
          >
            <div className="space-y-1 rounded-2xl border border-zinc-200 bg-white p-2 backdrop-blur-xl dark:border-white/10 dark:bg-zinc-900/60">
              {sections.map((section) => renderNavButton(section, true))}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="flex gap-6">
        <div className="hidden w-64 flex-shrink-0 lg:block">
          <div className="sticky top-6 space-y-1 rounded-2xl border border-zinc-200 bg-white p-3 backdrop-blur-xl dark:border-white/10 dark:bg-zinc-900/60">
            {sections.map((section) => renderNavButton(section))}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          {loading && !shop ? (
            <LoadingState lines={6} />
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <Outlet context={{ shop, shopId, refreshShop: loadShop, shopLoading: loading }} />
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminShopWorkspace;
